import * as React from 'react';
import {useState, useTransition} from 'react';
import {graphql, useLazyLoadQuery, useMutation} from 'react-relay';
import {Link, useParams} from 'react-router-dom';
import {ArrowLeft, Trash2} from 'lucide-react';
import ScanInput from './ScanInput';
import {OpsKitPageQuery} from './__generated__/OpsKitPageQuery.graphql';
import {OpsKitPageUpdateMutation} from './__generated__/OpsKitPageUpdateMutation.graphql';
import {OpsKitPageAddMemberMutation} from './__generated__/OpsKitPageAddMemberMutation.graphql';
import {OpsKitPageRemoveMemberMutation} from './__generated__/OpsKitPageRemoveMemberMutation.graphql';
import {dangerButton, ghostButton, inputClass, primaryButton} from './lib';

const QUERY = graphql`
    query OpsKitPageQuery($id: ID!) {
        node(id: $id) {
            ... on Asset {
                id
                assetTag
                category
                description
                dailyRate
                rentable
                status
                notes
                kitMembers {
                    id
                    assetTag
                    name
                    category
                    status
                    serial
                }
            }
        }
    }`;

const UPDATE = graphql`mutation OpsKitPageUpdateMutation($input: UpdateKitInput!) { updateKit(input: $input) { kit { id } userErrors { message } } }`;
const ADD_MEMBER = graphql`mutation OpsKitPageAddMemberMutation($input: AddKitMemberInput!) { addKitMember(input: $input) { kit { id } userErrors { message } } }`;
const REMOVE_MEMBER = graphql`mutation OpsKitPageRemoveMemberMutation($input: RemoveKitMemberInput!) { removeKitMember(input: $input) { kit { id } userErrors { message } } }`;

type Payload = { kit: { id: string } | null; userErrors: ReadonlyArray<{ message: string }> } | null | undefined;
const settle = (payload: Payload): string | null =>
    payload?.kit ? null : payload?.userErrors.map((u) => u.message).join('; ') || 'Something went wrong';

const STATUSES = ['AVAILABLE', 'IN_USE', 'MAINTENANCE', 'LOST', 'RETIRED'] as const;

// Edit a kit's details and scan members in and out of it.
const OpsKitPage: React.FC = () => {
    const {id = ''} = useParams();
    const [fetchKey, setFetchKey] = useState(0);
    const [, startTransition] = useTransition();
    const data = useLazyLoadQuery<OpsKitPageQuery>(QUERY, {id: decodeURIComponent(id)}, {fetchKey, fetchPolicy: 'network-only'});
    const kit = data.node;

    const [update, updating] = useMutation<OpsKitPageUpdateMutation>(UPDATE);
    const [addMember] = useMutation<OpsKitPageAddMemberMutation>(ADD_MEMBER);
    const [removeMember] = useMutation<OpsKitPageRemoveMemberMutation>(REMOVE_MEMBER);

    const [banner, setBanner] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({description: '', dailyRate: '', notes: '', rentable: true, status: 'AVAILABLE'});

    if (!kit || !kit.id || kit.category !== 'KIT') {
        return <p className="text-gray-500">Kit not found. <Link to="/ops/inventory" className="text-lumina-purple">Back to inventory</Link></p>;
    }

    const refresh = () => startTransition(() => setFetchKey((k) => k + 1));

    const run = <T, >(commit: (config: any) => void, input: Record<string, unknown>, pick: (res: T) => Payload) =>
        new Promise<string | null>((resolve) => {
            commit({
                variables: {input: {kitId: kit.id, ...input}},
                onCompleted: (res: T) => {
                    const error = settle(pick(res));
                    if (!error) refresh();
                    resolve(error);
                },
                onError: (err: Error) => resolve(err.message),
            });
        });

    const startEditing = () => {
        setForm({
            description: kit.description ?? '',
            dailyRate: kit.dailyRate ?? '',
            notes: kit.notes ?? '',
            rentable: kit.rentable,
            status: kit.status,
        });
        setEditing(true);
    };

    const save = async (e: React.FormEvent) => {
        e.preventDefault();
        const rate = form.dailyRate.trim() === '' ? 0 : Number(form.dailyRate);
        if (Number.isNaN(rate) || rate < 0) {
            setBanner('Daily rate must be a number');
            return;
        }
        const error = await run<any>(update, {
            description: form.description.trim(),
            dailyRate: rate,
            notes: form.notes.trim() || null,
            rentable: form.rentable,
            status: form.status,
        }, (res) => res.updateKit);
        setBanner(error);
        if (!error) setEditing(false);
    };

    const members = [...kit.kitMembers].sort((a, b) => a.assetTag.localeCompare(b.assetTag));

    return (
        <div>
            <Link to="/ops/inventory" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-white mb-4">
                <ArrowLeft className="w-3 h-3"/> Inventory
            </Link>

            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                <div>
                    <p className="font-mono text-xs text-gray-500">{kit.assetTag}</p>
                    <h1 className="text-3xl font-display font-bold">{kit.description || 'Untitled kit'}</h1>
                    <p className="text-sm text-gray-400 mt-1">
                        {kit.dailyRate ? `$${kit.dailyRate}/day` : 'No rate'} · {kit.rentable ? 'Rentable' : 'Internal'} · {kit.status.replace(/_/g, ' ')}
                        {' · '}{members.length} piece{members.length === 1 ? '' : 's'}
                    </p>
                    {kit.notes && <p className="text-sm text-gray-500 mt-2 whitespace-pre-line">{kit.notes}</p>}
                </div>
                {!editing && <button onClick={startEditing} className={ghostButton}>Edit details</button>}
            </div>

            {banner && (
                <div className="mb-4 text-sm text-red-400 border border-red-500/30 bg-red-500/5 px-3 py-2">{banner}</div>
            )}

            {editing && (
                <form onSubmit={save} className="border border-gray-800 p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                    <label className="block md:col-span-2">
                        <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Description</span>
                        <input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required className={`${inputClass} w-full`}/>
                    </label>
                    <label className="block">
                        <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Daily rate ($, blank for none)</span>
                        <input value={form.dailyRate} onChange={(e) => setForm({...form, dailyRate: e.target.value})} inputMode="decimal" className={`${inputClass} w-full`}/>
                    </label>
                    <label className="block">
                        <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Status</span>
                        <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className={`${inputClass} w-full`}>
                            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                        </select>
                    </label>
                    <label className="block md:col-span-2">
                        <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Notes</span>
                        <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} className={`${inputClass} w-full`}/>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300 md:col-span-2">
                        <input type="checkbox" checked={form.rentable} onChange={(e) => setForm({...form, rentable: e.target.checked})}/>
                        Offered for rent on the public page
                    </label>
                    <div className="flex gap-3 md:col-span-2">
                        <button type="submit" disabled={updating} className={primaryButton}>Save</button>
                        <button type="button" onClick={() => setEditing(false)} className={ghostButton}>Cancel</button>
                    </div>
                </form>
            )}

            <h2 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Members</h2>
            <div className="max-w-xl mb-4">
                <ScanInput
                    placeholder="Scan or type an asset tag to add it to this kit"
                    onScan={(tag) => run<any>(addMember, {assetTag: tag}, (res) => res.addKitMember)}
                />
            </div>
            {members.length === 0 ? (
                <p className="text-sm text-gray-500">No members yet. Scan the items that belong in this kit.</p>
            ) : (
                <div className="overflow-x-auto border border-gray-800 max-w-4xl">
                    <table className="w-full text-sm">
                        <thead className="bg-black/50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                        <tr>
                            <th className="px-3 py-2">Tag</th>
                            <th className="px-3 py-2">Name</th>
                            <th className="px-3 py-2">Category</th>
                            <th className="px-3 py-2">Serial</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-900">
                        {members.map((m) => (
                            <tr key={m.id} className="hover:bg-white/[0.03]">
                                <td className="px-3 py-2 font-mono text-xs text-gray-300 whitespace-nowrap">{m.assetTag}</td>
                                <td className="px-3 py-2 text-white">{m.name}</td>
                                <td className="px-3 py-2 text-xs text-gray-400">{m.category.replace(/_/g, ' ')}</td>
                                <td className="px-3 py-2 font-mono text-xs text-gray-500">{m.serial ?? '—'}</td>
                                <td className="px-3 py-2 text-xs text-gray-400">{m.status.replace(/_/g, ' ')}</td>
                                <td className="px-3 py-2 text-right">
                                    <button
                                        onClick={async () => setBanner(await run<any>(removeMember, {assetTag: m.assetTag}, (res) => res.removeKitMember))}
                                        className={`${dangerButton} inline-flex items-center gap-1 !px-2 !py-1`}
                                        title="Remove from kit"
                                    >
                                        <Trash2 className="w-3 h-3"/> Remove
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="text-xs text-gray-500 mt-3">Members can't change while the kit is on an open reservation.</p>
        </div>
    );
};

export default OpsKitPage;
